import type { ComponentType } from 'react';
import { useLingui } from '@lingui/react/macro';
import { GlobeHeartsIllustration } from '@worldcoo/ui/illustrations/static/globe-hearts';
import { PosDonationIllustration } from '@worldcoo/ui/illustrations/static/pos-donation';

/**
 * A `photo` fills the card; its `src` may be per locale (`en` required) because 2025's is a phone screen in the
 * reader's language. An `illustration` has its own `width` (the globe is 312x316, the terminal 255x318). A
 * `screen` darkens the plate and anchors the capture bottom-right.
 */
export type MilestoneArt =
	| { kind: 'photo'; src: string | (Record<string, string> & { en: string }); alt: string }
	| { kind: 'illustration'; art: ComponentType<{ className?: string; label?: string }>; alt: string; width: string }
	| { kind: 'screen'; src: string; alt: string; width: string };

/**
 * `id` because 2025 holds two milestones, and not the caption, which is translated and would remount every
 * slide on a language change. `caption` and `image` are optional: the hand-off spells out three of the seven
 * years, and an invented milestone would be worse than a visibly unfinished one.
 */
export type Milestone = { id: string; year: string; caption?: string; image?: MilestoneArt };

/** A hook, not a constant: `t` only extracts and only re-runs on a language change inside a component. */
export function useMilestones(): Milestone[] {
	const { t } = useLingui();
	return [
		{
			id: 'born',
			year: '2012',
			caption: t`We were born in Barcelona`,
			image: { kind: 'photo', src: '/redesign/about/barcelona-skyline.jpg', alt: t`Barcelona seen from above` },
		},
		{
			id: 'ecommerce',
			year: '2014',
			caption: t`We launched the Solidarity Euro for ecommerce`,
			image: { kind: 'photo', src: '/redesign/about/laptop-checkout.jpg', alt: t`Someone shopping online on a laptop` },
		},
		{
			id: 'stores',
			year: '2017',
			caption: t`We moved into physical stores with Charity Rounding Up`,
			image: {
				kind: 'photo',
				src: '/redesign/about/contactless-terminal.jpg',
				alt: t`A shopper paying by contactless card at a shop counter`,
			},
		},
		{
			id: 'million',
			year: '2018',
			caption: t`We reached one million euros raised`,
			image: {
				kind: 'illustration',
				art: PosDonationIllustration,
				alt: t`An illustration of euro coins dropping into a card terminal`,
				width: '60.2%', // 266 of 442: the height that overhangs as much as the globe does
			},
		},
		{
			id: 'expansion',
			year: '2022',
			caption: t`We expanded into Andorra and Portugal`,
			image: {
				kind: 'illustration',
				art: GlobeHeartsIllustration,
				alt: t`An illustration of the globe with two hearts over Europe`,
				width: '70.6%', // the export's own 312 of 442
			},
		},
		{
			id: 'app',
			year: '2025',
			caption: t`We launched our app for donors`,
			// Only `en` exists so far; the other locales fall back to it. The alt is the Solutions page's msgid.
			image: {
				kind: 'photo',
				src: { en: '/redesign/about/thanks-screen-en.jpg' },
				alt: t`The Worldcoo app thanking a donor for their donation`,
			},
		},
		{
			id: 'italy',
			// The second 2025: why the rail is a year axis, not a slide list.
			year: '2025',
			caption: t`We achieved our first success story in Italy`,
			image: {
				kind: 'photo',
				src: '/redesign/about/italy-flag.jpg',
				alt: t`The Italian flag flying against a blue sky`,
			},
		},
		{
			id: 'plus',
			year: '2026',
			caption: t`We launched Worldcoo Plus, a new service for NGOs`,
			image: {
				kind: 'screen',
				src: '/redesign/about/plus-dashboard.svg',
				alt: t`The Worldcoo Plus dashboard for NGOs`,
				width: '84.3%', // measured off the design: 458 of the card's 543
			},
		},
	];
}
